import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCartStore } from '../store/index'
import { PaymentService, PaymentMethodSelector, PaymentForm, PaymentStatus } from '../services/paymentService.jsx'
import Header from '../components/Header'
import Footer from '../components/Footer'

const CheckoutPage = () => {
  const navigate = useNavigate()
  const { items, total, clearCart } = useCartStore()
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    paymentMethod: 'stripe'
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState(null)
  const [transactionId, setTransactionId] = useState(null)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.firstName) newErrors.firstName = 'First name is required'
    if (!formData.lastName) newErrors.lastName = 'Last name is required'
    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid'
    }
    if (!formData.phone) newErrors.phone = 'Phone number is required'
    if (!formData.address) newErrors.address = 'Address is required'
    if (!formData.city) newErrors.city = 'City is required'
    if (!formData.postalCode) newErrors.postalCode = 'Postal code is required'
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }
    
    setLoading(true)
    
    try {
      // Generate order ID
      const orderId = `ORD-${Date.now()}`
      
      // Prepare payment data
      const paymentData = {
        method: formData.paymentMethod,
        amount: total,
        orderId,
        customerInfo: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          postalCode: formData.postalCode
        }
      }
      
      // Process payment
      const paymentResult = await PaymentService.processPayment(paymentData)
      
      // Create payment record
      const paymentRecord = await PaymentService.createPaymentRecord(orderId, {
        ...paymentData,
        transactionId: paymentResult.transactionId,
        gatewayResponse: paymentResult
      })
      
      setTransactionId(paymentResult.transactionId)
      setPaymentStatus('success')
      
      // Clear cart
      clearCart()
      
      // Navigate to order confirmation after 3 seconds
      setTimeout(() => {
        navigate(`/order/${orderId}`)
      }, 3000)
      
    } catch (error) {
      console.error('Payment error:', error)
      setPaymentStatus('failed')
    } finally {
      setLoading(false)
    }
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="page-container py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Your Cart is Empty</h1>
            <p className="text-gray-600 mb-8">Please add some items to your cart before checkout.</p>
            <Link to="/products" className="btn-primary">
              Continue Shopping
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <Header />
      
      <div className="page-container py-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Checkout Form */}
              <div>
                <h2 className="text-xl font-semibold mb-6">Billing Information</h2>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">First Name</label>
                      <input
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`input-field ${errors.firstName ? 'input-error' : ''}`}
                        placeholder="Enter your first name"
                      />
                      {errors.firstName && (
                        <p className="error-text">{errors.firstName}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="label">Last Name</label>
                      <input
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`input-field ${errors.lastName ? 'input-error' : ''}`}
                        placeholder="Enter your last name"
                      />
                      {errors.lastName && (
                        <p className="error-text">{errors.lastName}</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="label">Email</label>
                    <input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`input-field ${errors.email ? 'input-error' : ''}`}
                      placeholder="your.email@example.com"
                    />
                    {errors.email && (
                      <p className="error-text">{errors.email}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="label">Phone Number</label>
                    <input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`input-field ${errors.phone ? 'input-error' : ''}`}
                      placeholder="+880 1234 567890"
                    />
                    {errors.phone && (
                      <p className="error-text">{errors.phone}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="label">Address</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      rows={3}
                      className={`input-field ${errors.address ? 'input-error' : ''}`}
                      placeholder="Enter your full address"
                    />
                    {errors.address && (
                      <p className="error-text">{errors.address}</p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">City</label>
                      <input
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className={`input-field ${errors.city ? 'input-error' : ''}`}
                        placeholder="Enter your city"
                      />
                      {errors.city && (
                        <p className="error-text">{errors.city}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="label">Postal Code</label>
                      <input
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleChange}
                        className={`input-field ${errors.postalCode ? 'input-error' : ''}`}
                        placeholder="Enter postal code"
                      />
                      {errors.postalCode && (
                        <p className="error-text">{errors.postalCode}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Summary & Payment */}
              <div>
                <div className="card sticky top-8">
                  <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
                  
                  <div className="space-y-3 mb-6">
                    {items.map((item) => (
                      <div key={item.id} className="flex justify-between">
                        <span>{item.name} x {item.quantity}</span>
                        <span>৳{(item.price * item.quantity).toLocaleString()}</span>
                      </div>
                    ))}
                    <div className="border-t pt-3">
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total</span>
                        <span>৳{total.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  
                      <div className="mb-6">
                        <PaymentMethodSelector
                          selectedMethod={formData.paymentMethod}
                          onMethodChange={(method) => setFormData(prev => ({ ...prev, paymentMethod: method }))}
                        />
                      </div>
                      
                      {paymentStatus ? (
                        <PaymentStatus
                          status={paymentStatus}
                          transactionId={transactionId}
                          amount={total}
                          method={formData.paymentMethod}
                        />
                      ) : (
                        <button
                          type="submit"
                          disabled={loading}
                          className="btn-primary w-full mb-4"
                        >
                          {loading ? (
                            <div className="flex items-center justify-center">
                              <div className="loading-spinner mr-2"></div>
                              Processing...
                            </div>
                          ) : (
                            `Pay ৳${total.toLocaleString()}`
                          )}
                        </button>
                      )}
                  
                  <Link to="/cart" className="btn-secondary w-full text-center block">
                    Back to Cart
                  </Link>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default CheckoutPage
